/**
 * `/home` — the practice Home. Briefing and By Type are three panes: the For
 * Review inbox on the left, the view (or the selected item) in the center,
 * and the collapsible Calendar / Activity rail on the right (a sheet below
 * `xl`). The Dashboard takes the full width under its greeting header.
 * Paints from the browser cache with skeletons only on a true first load;
 * a thin progress bar shows while the summary refreshes. `Empty` when the
 * caller has no practice.
 */
import { Building2, X } from "lucide-react";
import { useCallback, useMemo, useState, type FC, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { HeaderIconButton } from "@/components/app-header";
import { EmptyState } from "@/components/ui/empty-state";
import { KINDS } from "@/components/ui/kind-icon";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { AgentScope } from "@/features/agents/types";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { TimeHomeCards } from "@/features/timeTracking/home/TimeHomeCards";
import { cn } from "@/lib/utils";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import { useMatches } from "../components/DashboardShell";
import { DraftComposer } from "../components/DraftComposer";
import { RecommendedActions } from "../components/RecommendedActions";
import { LastUpdated } from "../components/LastUpdated";
import { useDashboardLayout } from "../dashboardLayoutStore";
import { briefingLead, greetingLine } from "../home/briefingCopy";
import { BriefingView } from "../home/BriefingView";
import { ByTypeView } from "../home/ByTypeView";
import { DashboardView } from "../home/DashboardView";
import { ForReviewRail } from "../home/ForReviewRail";
import { HomeGreetingHeader } from "../home/HomeGreetingHeader";
import { groupByKind, runwayMarks } from "../home/homeKinds";
import { HomeRunway } from "../home/HomeRunway";
import { HomeViewBar } from "../home/HomeViewBar";
import { NavArrows, RailReopenButton } from "../home/PaneBar";
import { RightRail } from "../home/RightRail";
import { TriageDetail } from "../home/TriageDetail";
import { buildDated } from "../home/triageModel";
import { useBriefing } from "../home/useBriefing";
import { BRIEFING_ID, useHomeTriage } from "../home/useHomeTriage";
import { practiceAlerts } from "../practiceSuggestions";
import type { DashboardScope } from "../types";
import { useDashboardDraft } from "../useDashboardDraft";
import { useDashboardSuggestions } from "../useDashboardSuggestions";
import { useDashboardSummary } from "../useDashboardSummary";
import { useRecommendations } from "../useRecommendations";

/** Below this the center pane is too narrow to sit beside both rails. */
const RAIL_INLINE_QUERY = "(min-width: 1280px)";

/** Thin primary bar along the top while the summary refreshes. */
const RefreshBar: FC<{ active: boolean }> = ({ active }) => (
  <div
    aria-hidden
    data-testid="home-refresh-bar"
    data-active={active ? "true" : "false"}
    className={cn(
      "pointer-events-none absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden transition-opacity",
      active ? "opacity-100" : "opacity-0"
    )}
  >
    <div className="h-full w-1/3 animate-[home-progress_1.2s_ease-in-out_infinite] bg-primary" />
  </div>
);

const HomeBody: FC<{ practice: AgentScope }> = ({ practice }) => {
  const navigate = useNavigate();
  const uid = useSelector(lawyerSelectors.selectUid) as string | null;
  const firstName = useSelector(lawyerSelectors.selectFirstName) as
    | string
    | null;
  const fullName = useSelector(lawyerSelectors.selectFullName) as string | null;
  const scope = useMemo<DashboardScope>(
    () => ({ kind: "practice", id: practice.id }),
    [practice.id]
  );
  const summary = useDashboardSummary(scope);
  const recommendations = useRecommendations(scope);
  // Real alerts only: the generated chips can name any case and drift into
  // case prep. Alerts already read as "Needs You Today", so the chips under
  // the composer are the curated practice questions.
  const alerts = useMemo(
    () => practiceAlerts(recommendations.items),
    [recommendations.items]
  );
  const suggestions = useDashboardSuggestions({
    mode: "practice",
    items: recommendations.items,
    isLoading: recommendations.isLoading,
    regenerate: recommendations.regenerate,
    summary: summary.data,
    summaryLoading: summary.isLoading,
    includeAlerts: false
  });
  const briefing = useBriefing(scope.id);
  const draft = useDashboardDraft("practice", practice);
  const triage = useHomeTriage(summary.data, uid);

  const isXl = useMatches(RAIL_INLINE_QUERY);
  const railCollapsed = useDashboardLayout((s) => s.railCollapsed);
  const setRailCollapsed = useDashboardLayout((s) => s.setRailCollapsed);
  const [railSheetOpen, setRailSheetOpen] = useState(false);
  const railHidden = !isXl || railCollapsed;
  const reopen = () =>
    isXl ? setRailCollapsed(false) : setRailSheetOpen(true);
  const collapse = () =>
    isXl ? setRailCollapsed(true) : setRailSheetOpen(false);

  const firstLoad = summary.isLoading && !summary.data;

  const view = useDashboardLayout((s) => s.homeView);
  const setView = useDashboardLayout((s) => s.setHomeView);
  const layout = useDashboardLayout((s) => s.homeLayout);
  const setLayout = useDashboardLayout((s) => s.setHomeLayout);
  const homeScope = useDashboardLayout((s) => s.homeScope);
  const setHomeScope = useDashboardLayout((s) => s.setHomeScope);
  const runwayDays = useDashboardLayout((s) => s.runwayDays);
  const setRunwayDays = useDashboardLayout((s) => s.setRunwayDays);
  const homeProperties = useDashboardLayout((s) => s.homeProperties);
  const fullWidth = view === "dashboard";

  const trailing =
    railHidden && !fullWidth ? <RailReopenButton onClick={reopen} /> : null;

  const scopedItems = useMemo(
    () =>
      homeScope === "mine"
        ? triage.items.filter((t) => t.assignedToMe)
        : triage.items,
    [triage.items, homeScope]
  );
  const dated = useMemo(
    () => buildDated(summary.data, uid ?? ""),
    [summary.data, uid]
  );
  const scopedDated = useMemo(
    () => (homeScope === "mine" ? dated.filter((t) => t.assignedToMe) : dated),
    [dated, homeScope]
  );
  const briefingMarks = useMemo(() => runwayMarks(dated), [dated]);
  const hiddenTypes =
    view === "byType" && layout === "horizontal" && scopedItems.length
      ? groupByKind(scopedItems)
          .filter((g) => g.rows.length === 0)
          .map((g) => `${KINDS[g.kind].label} 0`)
      : [];

  const openFromRunway = (id: string, path: string) =>
    triage.items.some((t) => t.id === id) ? triage.select(id) : navigate(path);
  const changeView = (next: typeof view) => {
    triage.select(BRIEFING_ID);
    setView(next);
  };

  // Forced refresh: bypasses the server cache; the data on screen stays if
  // it fails.
  const { refresh: refreshSummary } = summary;
  const refresh = useCallback(
    () =>
      refreshSummary().catch((e: unknown) => {
        toast.error("Couldn't refresh the dashboard.", {
          description: e instanceof Error ? e.message : undefined
        });
      }),
    [refreshSummary]
  );

  const lastUpdated = (
    <LastUpdated
      updatedAt={summary.updatedAt}
      isFetching={summary.isRefreshing}
      isRefreshing={summary.isRefreshing}
      onRefresh={() => void refresh()}
    />
  );

  const viewBar = (
    <HomeViewBar
      view={view}
      onViewChange={changeView}
      scope={homeScope}
      onScopeChange={setHomeScope}
      layout={layout}
      onLayoutChange={setLayout}
      hint={hiddenTypes.length ? `Hidden: ${hiddenTypes.join(" · ")}` : ""}
      trailing={trailing}
    >
      {view === "briefing" ? (
        <NavArrows
          onPrev={() => undefined}
          onNext={() => triage.step(1)}
          canPrev={false}
          canNext={triage.canNext}
        />
      ) : null}
      {lastUpdated}
    </HomeViewBar>
  );

  let center: ReactNode;
  if (triage.selected && !firstLoad) {
    center = (
      <TriageDetail
        key={triage.selected.id}
        item={triage.selected}
        canPrev={triage.canPrev}
        canNext={triage.canNext}
        onPrev={() => triage.step(-1)}
        onNext={() => triage.step(1)}
        onRemove={triage.remove}
        onChanged={() => void refresh()}
        trailing={
          <>
            {view === "briefing" ? null : (
              <HeaderIconButton
                label="Close"
                onClick={() => triage.select(BRIEFING_ID)}
                data-testid="triage-close"
              >
                <X className="size-4" />
              </HeaderIconButton>
            )}
            {trailing}
          </>
        }
      />
    );
  } else if (view === "briefing") {
    center = (
      <BriefingView
        firstName={firstName}
        uid={uid}
        briefing={briefing.data}
        briefingLoading={briefing.isLoading}
        recommendations={alerts}
        items={triage.items}
        onOpenItem={triage.select}
        onPrompt={(text) => void draft.send(text)}
        canNext={triage.canNext}
        onNext={() => triage.step(1)}
        bar={viewBar}
        composer={
          <DraftComposer
            draftKey="dashboard:home"
            agentLabel="Casey"
            onSend={(text) => void draft.send(text)}
            isSending={draft.isSending}
            placeholder="Ask Casey about any client, case, or deadline"
          />
        }
        suggestions={
          <RecommendedActions
            items={suggestions.items}
            seed={practice.id}
            isLoading={suggestions.isLoading}
            onPrompt={(text) => void draft.send(text)}
            onRegenerate={suggestions.regenerate}
            align="start"
          />
        }
        runway={
          firstLoad ? null : (
            <HomeRunway
              marks={briefingMarks}
              lanes={["deadline", "expiration", "intake", "calendar"]}
              days={runwayDays}
              onDaysChange={setRunwayDays}
              onOpen={(m) => openFromRunway(m.id, m.path)}
            />
          )
        }
        topCards={<TimeHomeCards practice={practice} />}
      />
    );
  } else if (view === "dashboard") {
    center = (
      <DashboardView
        header={
          <HomeGreetingHeader
            greeting={greetingLine(firstName, new Date())}
            lead={briefingLead(briefing.data)}
            onReadBriefing={() => changeView("briefing")}
            view={view}
            onViewChange={changeView}
            scope={homeScope}
            onScopeChange={setHomeScope}
          >
            {lastUpdated}
          </HomeGreetingHeader>
        }
        items={scopedItems}
        dated={scopedDated}
        intakesInFlight={summary.data?.intakes_in_flight ?? []}
        scope={homeScope}
        uid={uid}
        runwayDays={runwayDays}
        onRunwayDaysChange={setRunwayDays}
        onOpen={triage.select}
        onOpenKind={() => setView("byType")}
      />
    );
  } else {
    center = (
      <>
        {viewBar}
        <ByTypeView
          items={scopedItems}
          layout={layout}
          onLayoutChange={setLayout}
          onOpen={triage.select}
          properties={homeProperties}
          meName={fullName?.trim() || firstName}
        />
      </>
    );
  }

  return (
    <TooltipProvider>
      <div
        className="relative flex h-full min-h-0 min-w-0 flex-1"
        data-testid="home-layout"
      >
        <RefreshBar active={summary.isRefreshing} />
        {fullWidth ? null : (
          <ForReviewRail
            triage={triage}
            isLoading={firstLoad}
            briefingChanges={briefing.data?.changes_count ?? null}
            briefingAt={briefing.data?.generated_at ?? null}
            briefingSelected={view === "briefing"}
            onOpenBriefing={() => setView("briefing")}
            className="hidden md:flex"
          />
        )}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col bg-surface">
          {center}
          {summary.error && !summary.data ? (
            <p
              className="border-t border-line-2 px-4 py-2 text-sm text-danger"
              data-testid="home-error"
            >
              {summary.error}
            </p>
          ) : null}
        </main>
        {isXl && !railCollapsed && !fullWidth ? (
          <RightRail scope={scope} onCollapse={collapse} />
        ) : null}
        {!isXl && !fullWidth ? (
          <Sheet open={railSheetOpen} onOpenChange={setRailSheetOpen}>
            <SheetContent side="right" className="w-85 p-0 sm:max-w-sm">
              <SheetTitle className="sr-only">Calendar and Activity</SheetTitle>
              <RightRail scope={scope} className="w-full border-l-0" />
            </SheetContent>
          </Sheet>
        ) : null}
      </div>
    </TooltipProvider>
  );
};

export const HomePage: FC = () => {
  const { practiceScope, loading, error } = useAgentSpecs();

  if (loading && !practiceScope) {
    // Specs are restored from cache almost always; the body shows its own
    // skeletons once the scope is known.
    return <div className="h-full" data-testid="home-loading" />;
  }
  if (!practiceScope) {
    return (
      <EmptyState
        icon={Building2}
        title="No Practice Yet"
        description={
          error ??
          "Home shows what needs attention across a firm. Join or set up a practice to use it."
        }
        className="h-full"
        data-testid="home-empty"
      />
    );
  }
  return <HomeBody practice={practiceScope} />;
};
